import { useEffect, useMemo, useRef } from "react";
import { TextField as MuiTextField } from "@mui/material";
import type { ProcessedFormField } from "@/types/api/formSchema";
import {
  formatDateISO,
  resolveDateConstraints,
  resolveDefaultValue,
} from "@/lib/dateConstraints";

type Props = {
  field: ProcessedFormField;
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  name: string;
};

export function DateField({
  field: schemaField,
  value,
  onChange,
  onBlur,
  name,
}: Props) {
  const hasConstraints = !!(
    schemaField.constraints?.min_date ||
    schemaField.constraints?.max_date ||
    schemaField.constraints?.disabled_days?.length ||
    schemaField.constraints?.disabled_dates?.length ||
    schemaField.constraints?.default_value
  );

  const { minDate, maxDate } = useMemo(
    () => resolveDateConstraints(schemaField.constraints),
    [schemaField.constraints],
  );

  const resolvedDefault = useMemo(() => {
    if (!hasConstraints) return undefined;
    return resolveDefaultValue(schemaField.constraints, minDate);
  }, [hasConstraints, schemaField.constraints, minDate]);

  const defaultedRef = useRef(false);
  useEffect(() => {
    if (!value && !defaultedRef.current && resolvedDefault) {
      defaultedRef.current = true;
      onChange(resolvedDefault);
    }
  }, [value, onChange, resolvedDefault]);

  return (
    <MuiTextField
      id={name}
      name={name}
      type="date"
      value={value ?? ""}
      disabled={!!schemaField.disabled}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      inputProps={{
        min: minDate ? formatDateISO(minDate) : undefined,
        max: maxDate ? formatDateISO(maxDate) : undefined,
      }}
      fullWidth
      size="small"
      variant="outlined"
      InputLabelProps={{ shrink: true }}
    />
  );
}
